import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../services/useAuth';
import { Loading } from '../../components/Loading';
import { useAtom } from 'jotai';
import { getMyHackathons, HackathonPayload, hackathonsAtom } from '../../services/hackathon.service';
import { HackathonSummary } from './HackathonSummary';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../components/PageContainer';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Button from '@mui/material/Button/Button';
import Box from '@mui/material/Box/Box';
import AddIcon from '@mui/icons-material/Add';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { uniqBy } from 'lodash';
import { getHackathonStatus, lastActivity } from '../../utils/hackathonStatus';
import { HelpLink } from '../../components/HelpLink';
import { OrganizationHistory } from '../../components/Organization/OrganizationHistory';

// an event with anything happening this recently is the one people are looking for today
const HAPPENING_MS = 3 * 60 * 60 * 1000;

const HackathonGrid = ({ hackathons }: { hackathons: HackathonPayload[] }) => (
    <Box sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }
    }}>
        {
            hackathons.map(h => (
                <HackathonSummary key={`my-hackathon-${h._id}`} hackathon={h} />
            ))
        }
    </Box>
)

export const MyHackathons = () => {
    const { user, loading } = useAuth();

    const [hackathons] = useAtom(hackathonsAtom);

    const navigate = useNavigate();

    const [loadingHackathons, setLoadingHackathons] = useState(false);
    // finished events pile up; keep them out of the way of the ones happening now
    const [showPast, setShowPast] = useState(false);

    useEffect(() => {
        if (user) {
            setLoadingHackathons(true);
            getMyHackathons().then(() => {
                setLoadingHackathons(false);
            }, (err) => {
                console.error(err);
                setLoadingHackathons(false);
            });
        }
    }, [user])

    const { happening, active, past } = useMemo(() => {
        const list: HackathonPayload[] = uniqBy(Object.values(hackathons), "_id");
        const byStartDesc = (a: HackathonPayload, b: HackathonPayload) =>
            (b.startDate?.getTime() || 0) - (a.startDate?.getTime() || 0);
        // the event you just created or joined is the one you're looking for, whatever its dates
        const byNewest = (a: HackathonPayload, b: HackathonPayload) =>
            (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0) || byStartDesc(a, b);
        const isPast = (h: HackathonPayload) => ['ended', 'results'].includes(getHackathonStatus(h).phase);
        const isHappening = (h: HackathonPayload) => Date.now() - (lastActivity(h)?.getTime() || 0) < HAPPENING_MS;
        const current = list.filter(h => !isPast(h)).sort(byNewest);
        return {
            happening: current.filter(isHappening),
            active: current.filter(h => !isHappening(h)),
            past: list.filter(isPast).sort(byStartDesc)
        };
    }, [hackathons])

    if (loading) {
        return <Loading />
    }

    if (!user) {
        return <PageContainer>
            <h1>Not logged in</h1>
        </PageContainer>
    }

    const empty = happening.length === 0 && active.length === 0 && past.length === 0;

    return <PageContainer variant="plain">
        <Stack spacing={4}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <div>
                    <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>My hackathons</Typography>
                    <Typography color="text.secondary">Events you're organizing, judging or taking part in.</Typography>
                </div>
                <HelpLink guide="start">How Hack in a Box works</HelpLink>
            </Stack>

            {loadingHackathons && empty && <Loading />}

            {
                !loadingHackathons && empty && (
                    <Card sx={{ borderStyle: 'dashed' }}>
                        <CardContent sx={{ py: 6, textAlign: 'center' }}>
                            <Stack spacing={2} alignItems="center">
                                <RocketLaunchIcon color="primary" sx={{ fontSize: 48 }} />
                                <Typography variant="h3" component="h2">Let us help you organize a hackathon</Typography>
                                <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
                                    Create one in a few minutes, or ask an organizer for the link to theirs.
                                </Typography>
                                <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => navigate('/new')}>Create a hackathon</Button>
                            </Stack>
                        </CardContent>
                    </Card>
                )
            }

            {
                happening.length > 0 && (
                    <Stack spacing={2}>
                        <Typography variant="overline" color="primary">Happening now</Typography>
                        <HackathonGrid hackathons={happening} />
                    </Stack>
                )
            }

            {
                active.length > 0 && (
                    <Stack spacing={2}>
                        <Typography variant="overline" color="text.secondary">{happening.length ? 'Quiet lately' : 'Active & upcoming'}</Typography>
                        <HackathonGrid hackathons={active} />
                    </Stack>
                )
            }

            {
                past.length > 0 && (
                    <Stack spacing={2} alignItems="flex-start">
                        <Button variant="text" onClick={() => setShowPast(!showPast)} aria-expanded={showPast}>
                            {showPast ? `Hide past hackathons (${past.length})` : `Show past hackathons (${past.length})`}
                        </Button>
                        {showPast && <Box sx={{ width: '100%' }}><HackathonGrid hackathons={past} /></Box>}
                    </Stack>
                )
            }

            {/* what your organizations ran before, that you weren't in */}
            <OrganizationHistory />
        </Stack>
    </PageContainer>
}
